<template>
  <div class="profesor-upload-container">
    <h3 class="title">Ingreso Masivo de Profesores</h3>

    <div class="upload-section">
      <label class="file-label">
        <span>Seleccionar archivo Excel (.xlsx, .xls)</span>
        <input
          type="file"
          @change="handleFileUpload"
          accept=".xlsx, .xls"
          class="file-input"
        />
      </label>
    </div>

    <div v-if="excelData.length" class="progress-section">
      <div class="progress-info">
        <span>Progreso: {{ processedCount }} / {{ excelData.length }}</span>
        <span>{{ currentStatus }}</span>
      </div>
      <progress :value="processedCount" :max="excelData.length"></progress>
      <button
        class="action-button"
        @click="procesarProfesores"
        :disabled="isProcessing"
      >
        {{ isProcessing ? 'Procesando...' : 'Iniciar Proceso' }}
      </button>

      <div v-if="errores.length" class="error-actions">
        <button class="error-button" @click="descargarErrores">
          Descargar Errores ({{ errores.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import ProfesorService from '@/services/UsuarioService';

export default {
  data() {
    return {
      excelData: [],
      isProcessing: false,
      processedCount: 0,
      currentStatus: '',
      errores: [],
      tipoUsuario: '',
      usuarioClonar: null,
      cursosSeleccionados: [],
      colegios: {
        'NO APLICA': { id_colegio: 0, direccion_calle: '', direccion_numero: 0 },
        'MINEDUC DEMO': {
          id_colegio: 1,
          direccion_calle: 'EJERCITO',
          direccion_numero: 557,
        },
        'COLEGIO ACONCAGUA': {
          id_colegio: 2,
          direccion_calle: 'Camino Troncal Antiguo',
          direccion_numero: 1940,
        },
        'COLEGIO SAN NICOLÁS VILLA ALEMANA': {
          id_colegio: 3,
          direccion_calle: 'Santa Sarah',
          direccion_numero: 1720,
        },
        'COLEGIO SAN NICOLÁS CANAL CHACAO': {
          id_colegio: 4,
          direccion_calle: 'Los Lunes',
          direccion_numero: 373,
        },
        'COLEGIO SAN NICOLÁS EL OLIVAR': {
          id_colegio: 5,
          direccion_calle: 'Tamarugal',
          direccion_numero: 575,
        },
      },
      // Mapeo de cursos
      cursosMap: {
        '1° BÁSICO': 1,
        '2° BÁSICO': 2,
        '3° BÁSICO': 3,
        '4° BÁSICO': 4,
        '5° BÁSICO': 5,
        '6° BÁSICO': 6,
        '7° BÁSICO': 7,
        '8° BÁSICO': 8,
        '1° MEDIO': 9,
        '2° MEDIO': 10,
        '3° MEDIO': 11,
        '4° MEDIO': 12,
      },
    };
  },
  created() {
    const { tipo, id_usuario, cursos } = this.$route.query;
    this.tipoUsuario = tipo || '';
    this.usuarioClonar = id_usuario || null;
    this.cursosSeleccionados = cursos ? cursos.split(',') : [];
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          // Resetear estado
          this.excelData = jsonData.slice(1).filter((row) => Array.isArray(row));
          this.processedCount = 0;
          this.errores = [];
          this.currentStatus = '';
        } catch (error) {
          console.error('Error al leer el archivo Excel:', error);
          alert('Error al procesar el archivo Excel. Asegúrate de que sea válido.');
        }
      };
      reader.readAsArrayBuffer(file);
    },

    async procesarProfesores() {
      if (this.isProcessing || !this.excelData.length) return;

      this.isProcessing = true;
      this.processedCount = 0;
      this.errores = [];

      try {
        for (let i = 0; i < this.excelData.length; i++) {
          const rowData = this.excelData[i];
          this.currentStatus = `Procesando profesor ${i + 1}...`;

          try {
            const result = await this.procesarFila(rowData);
            if (result.error) {
              this.errores.push([...rowData, result.error]);
            }
          } catch (error) {
            const errorMsg = error.message || 'Error desconocido';
            console.error('Error en fila:', errorMsg);
            this.errores.push([...rowData, errorMsg]);
          }

          this.processedCount++;
          await new Promise((resolve) => setTimeout(resolve, 100)); // Pausa para responsividad
        }

        this.currentStatus = `Proceso completado. ${
          this.excelData.length - this.errores.length
        } éxitos, ${this.errores.length} errores.`;
      } catch (error) {
        console.error('Error inesperado:', error);
        this.currentStatus = 'Error inesperado en el proceso';
      } finally {
        this.isProcessing = false;
      }
    },

    mapearCursosCasa(cursoRaw, cursosCasaRaw) {
      const curso = cursoRaw ? cursoRaw.toString().trim().toUpperCase() : '';
      const cursosCasa = cursosCasaRaw
        ? cursosCasaRaw.split(',').map(item => item.trim())
        : [];

      // Validar curso
      if (curso && !this.cursosMap[curso]) {
        throw new Error(
          `Curso inválido: "${curso}". Debe ser uno de: ${Object.keys(this.cursosMap).join(', ')}`
        );
      }

      // Crear el array de objetos para cursos_casa
      const idCurso = curso ? this.cursosMap[curso] : null;
      const cursosCasaArray = cursosCasa.length
        ? cursosCasa.map(casa => ({
            id_curso: idCurso,
            nombre: curso || '',
            casa,
          }))
        : curso
        ? [{ id_curso: idCurso, nombre: curso, casa: '' }]
        : [];

      return cursosCasaArray;
    },

    async procesarFila(rowData) {
      if (!Array.isArray(rowData)) {
        throw new Error('Fila inválida: los datos no son un arreglo');
      }

      // Mapear el nombre del colegio
      const nombreColegio = rowData[0]?.trim()?.toUpperCase() || '';
      const colegio = this.colegios[nombreColegio];
      if (!colegio) {
        throw new Error(
          `Nombre de colegio inválido: "${nombreColegio}". Debe ser uno de: ${Object.keys(
            this.colegios
          ).join(', ')}`
        );
      }

      // Limpiar el RUT
      const rutRaw = rowData[3] || '';
      const rutLimpio = rutRaw
        .replace(/\./g, '')
        .replace(/^-|-$/g, '')
        .replace(/\s/g, '');
      if (!rutLimpio.includes('-')) {
        throw new Error(
          `El RUT "${rutRaw}" debe incluir guion (por ejemplo, 9945870-4)`
        );
      }

      // Obtener Curso y Curso_casa
      const curso = rowData[1] ? rowData[1].toString().trim() : '';
      const cursosCasaRaw = rowData[2] ? rowData[2].toString().trim() : '';

      // Mapear cursos_casa
      const cursosCasaArray = this.mapearCursosCasa(curso, cursosCasaRaw);

      // Validar que haya al menos un curso o curso_casa
      if (!curso && !cursosCasaRaw) {
        throw new Error('Falta el campo obligatorio: Curso o Curso_casa');
      }

      const payload = {
        id_colegio: colegio.id_colegio,
        curso_casa: rowData[2] ? rowData[2].toString().charAt(0) : '',
        identificador_rut: rutLimpio,
        apellido_paterno: rowData[4] || '',
        apellido_materno: rowData[5] || '',
        nombre: rowData[6] || '',
        sexo: rowData[7] || '',
        email: rowData[8] || '',
        id_usuario: this.usuarioClonar ? parseInt(this.usuarioClonar, 10) : 0,
        cursos: this.cursosSeleccionados.length
          ? this.cursosSeleccionados
          : [rowData[1] || ''],
        id_region: 6,
        id_comuna: 45,
        celular: rowData[9] || '',
        direccion_calle: rowData[10] || colegio.direccion_calle,
        direccion_numero: rowData[11] || colegio.direccion_numero.toString(),
        cursos_casa: cursosCasaArray,
      };

      // Validaciones
      if (!payload.nombre) {
        throw new Error('Falta el campo obligatorio: nombre');
      }
      if (!payload.email) {
        throw new Error('Falta el campo obligatorio: correo');
      }
      if (!payload.identificador_rut) {
        throw new Error('Falta el campo obligatorio: RUT');
      }
      if (!payload.cursos || payload.cursos.length === 0) {
        throw new Error('Falta el campo obligatorio: cursos');
      }
      if (payload.id_colegio === null || payload.id_colegio === undefined) {
        throw new Error('Falta el campo obligatorio: id_colegio');
      }

      console.log('Enviando payload para profesor:', JSON.stringify(payload));
      const response = await ProfesorService.upsertProfesor(payload);
      if (response.data.Mensaje !== 'Profesor procesado con éxito') {
        throw new Error(
          response.data.Mensaje || 'Error desconocido en la respuesta'
        );
      }

      return { success: true };
    },

    descargarErrores() {
      if (!this.errores.length) return;

      const headers = [
        'Colegio',
        'Curso',
        'Curso Casa',
        'RUT',
        'Apellido Paterno',
        'Apellido Materno',
        'Nombre',
        'Sexo',
        'Email',
        'Celular',
        'Dirección Calle',
        'Dirección Número',
        'Error',
      ];
      const data = [headers, ...this.errores];

      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Errores');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'Errores_Profesores.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.profesor-upload-container {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
}

.title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
  text-align: center;
}

.upload-section {
  margin-bottom: 30px;
  text-align: center;
}

.file-label {
  display: inline-block;
  padding: 12px 24px;
  background-color: #4285f4;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-weight: bold;
}

.file-label:hover {
  background-color: #3367d6;
}

.file-input {
  display: none;
}

.progress-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
  color: #555;
}

progress {
  width: 100%;
  height: 10px;
  margin-bottom: 20px;
  border-radius: 5px;
}

progress::-webkit-progress-bar {
  background-color: #e9ecef;
  border-radius: 5px;
}

progress::-webkit-progress-value {
  background-color: #28a745;
  border-radius: 5px;
}

.action-button {
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.action-button:hover {
  background-color: #218838;
}

.action-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.error-actions {
  margin-top: 20px;
}

.error-button {
  display: block;
  width: 100%;
  padding: 10px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.error-button:hover {
  background-color: #c82333;
}
</style>